/**
 * Wraps a Supabase / PostgREST error into a real `Error` instance.
 *
 * Supabase returns plain error objects (`{ message, code, details, hint }`), not
 * `Error` instances. Throwing them raw has two failure modes:
 *   1. As an unhandled promise rejection they surface as `[object Object]`
 *      (the reason has no `.stack`, so there are no app frames in the overlay).
 *   2. `instanceof Error` checks in callers (e.g. error toasts) miss them.
 *
 * `apiError` preserves the readable `message`, copies the diagnostic fields
 * (`code` / `details` / `hint`) as own properties — so existing readers like
 * `components/library/NewPackModal.tsx`'s `extractErrorMessage` keep working —
 * and stashes the original object as `cause`.
 */
export function apiError(error: unknown): Error {
  const e = error as
    | { message?: unknown; code?: unknown; details?: unknown; hint?: unknown }
    | null
    | undefined;

  const message =
    e && typeof e.message === "string" && e.message ? e.message : "Request failed";

  return Object.assign(new Error(message), {
    name: "ApiError",
    code: e?.code,
    details: e?.details,
    hint: e?.hint,
    cause: e,
  });
}
